import React, { useMemo, useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Label,
} from 'recharts';
import { Market, MarketOption } from "../types.ts";
import { chartColors } from "../config/brand.ts";

interface ChartRow {
  timestamp: string;
  [optionName: string]: string | number;
}

interface MarketOptionsGraphProps {
  market: Market;
  data: ChartRow[];
  timeRange?: 'day' | 'week' | 'month' | 'all';
  /** Price = implied probability %; Volume = trade size (ZAR) per snapshot outcome. */
  mode?: 'price' | 'volume';
  formatZar?: (amount: number) => string;
  /** LMSR avg buy as % (0–100), keyed by market_option.id — reference lines only in price mode. */
  avgBuyByOptionPercent?: Record<number, number>;
}

export function MarketOptionsGraph({
  market,
  data,
  timeRange = 'all',
  mode = 'price',
  formatZar = (n) => `R${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
  avgBuyByOptionPercent,
}: MarketOptionsGraphProps) {
  const colors = chartColors;

  /** Narrow viewports: extra chart right margin + asymmetric wrapper padding so Y-axis % ticks inset from screen edge. */
  const [chartCompact, setChartCompact] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = () => setChartCompact(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const chartMargin = useMemo(
    () =>
      chartCompact
        ? { top: 5, right: 22, left: 10, bottom: 30 }
        : { top: 5, right: 10, left: 10, bottom: 30 },
    [chartCompact],
  );

  const maxVolumeValue = useMemo(() => {
    let m = 0;
    for (const row of data) {
      for (const opt of market.market_options ?? []) {
        const v = row[opt.name];
        const num = typeof v === 'number' ? v : parseFloat(String(v));
        if (!Number.isNaN(num)) m = Math.max(m, num);
      }
    }
    return m;
  }, [data, market.market_options]);

  const volumeDomainMax = Math.max(maxVolumeValue * 1.05, 1);

  const emptyVolumeCopy = mode === 'volume' && maxVolumeValue <= 0;
  const formatTick = (value: string, index: number) => {
    const date = new Date(value);
    const dataLength = data.length;

    const isEdgeTick = index === 0 || index === dataLength - 1;

    switch (timeRange) {
      case 'day': {
        const totalPoints = data.length;
        const interval = Math.max(Math.floor(totalPoints / 5), 1);

        if (index % interval !== 0 && !isEdgeTick) return '';

        const hours = date.getHours();

        if (hours === 0) return '12am';
        if (hours === 12) return '12pm';

        return hours > 12 ? `${hours - 12}pm` : `${hours}am`;
      }

      case 'week': {
        const totalPoints = data.length;
        const showIndexes = new Set([
          0,
          Math.floor(totalPoints / 2),
          totalPoints - 1,
        ]);

        if (!showIndexes.has(index)) return '';

        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const day = date.getDate();
        const dayOfWeek = dayNames[date.getDay()];

        return `${dayOfWeek} ${day}`;
      }

      case 'month': {
        const totalPoints = data.length;
        const interval = Math.max(Math.floor(totalPoints / 5), 1);

        if (index % interval !== 0 && !isEdgeTick) return '';

        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'short' });

        return `${month} ${day}`;
      }

      case 'all':
      default: {
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const year = date.getFullYear();
        const currentYear = new Date().getFullYear();

        const label = month;

        const monthIndex = date.getMonth();
        const prevDate = index > 0 ? new Date(data[index - 1].timestamp) : null;
        const prevMonth = prevDate ? prevDate.getMonth() : -1;

        if (monthIndex === prevMonth && !isEdgeTick) return '';

        return year !== currentYear ? `${label} '${year.toString().slice(-2)}` : label;
      }
    }
  };

  const CustomTick = ({
    x,
    y,
    payload,
    index,
  }: {
    x?: number;
    y?: number;
    payload?: { value: string };
    index?: number;
  }) => {
    if (!payload || index === undefined) return null;
    const text = formatTick(payload.value, index);
    if (!text) return null;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="#6B7280"
          fontSize={11}
        >
          {text}
        </text>
      </g>
    );
  };

  const CustomTooltipPrice = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{
      value: number;
      name: string;
      color: string;
    }>;
    label?: string;
  }) => {
    if (!active || !payload || !payload.length || !label) return null;

    const date = new Date(label);
    const formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="text-xs text-gray-500 mb-2">{formattedDate}</p>
        {payload.map((entry, idx: number) => {
          const opt = market.market_options?.find((o) => o.name === entry.name);
          return (
          <div key={idx} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 min-w-0">
              {opt?.image_url ? (
                <img
                  src={opt.image_url}
                  alt=""
                  className="w-5 h-5 rounded-full object-cover shrink-0"
                />
              ) : (
                <div
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: entry.color }}
                />
              )}
              <span className="text-sm font-medium truncate">{entry.name}</span>
            </div>
            <span className="text-sm font-bold">{entry.value.toFixed(1)}%</span>
          </div>
          );
        })}
      </div>
    );
  };

  const CustomTooltipVolume = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{
      value: number;
      name: string;
      color: string;
    }>;
    label?: string;
  }) => {
    if (!active || !payload || !payload.length || !label) return null;

    const date = new Date(label);
    const formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="text-xs text-gray-500 mb-2">{formattedDate}</p>
        {payload.map((entry, idx: number) => {
          const opt = market.market_options?.find((o) => o.name === entry.name);
          return (
          <div key={idx} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 min-w-0">
              {opt?.image_url ? (
                <img
                  src={opt.image_url}
                  alt=""
                  className="w-5 h-5 rounded-full object-cover shrink-0"
                />
              ) : (
                <div
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: entry.color }}
                />
              )}
              <span className="text-sm font-medium truncate">{entry.name}</span>
            </div>
            <span className="text-sm font-bold">{formatZar(entry.value)}</span>
          </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full h-[380px] bg-white pl-3 pr-6 pt-4 pb-4 md:p-4 rounded-lg relative">
      {emptyVolumeCopy && (
        <p className="absolute inset-x-3 md:inset-x-4 top-14 z-[1] text-center text-sm text-gray-500 pointer-events-none">
          No trade volume in this period for this chart range.
        </p>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-2 pr-1">
        {(market.market_options ?? []).map((option: MarketOption, index: number) => (
          <div key={option.id} className="flex items-center gap-1.5 text-xs text-gray-700 max-w-[min(100%,220px)]">
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: colors[index % colors.length] }}
            />
            {option.image_url ? (
              <img
                src={option.image_url}
                alt=""
                className="w-5 h-5 rounded-full object-cover shrink-0"
              />
            ) : null}
            <span className="truncate font-medium" title={option.name}>
              {option.name}
            </span>
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={chartMargin}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
          <XAxis
            dataKey="timestamp"
            tick={<CustomTick />}
            axisLine={{ stroke: '#E5E7EB', strokeWidth: 1 }}
            tickLine={false}
            interval={0}
          />
          {mode === 'price' ? (
            <YAxis
              tickFormatter={(value) => `${value}%`}
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 11 }}
              width={40}
            />
          ) : (
            <YAxis
              tickFormatter={(value) =>
                typeof value === 'number' ? formatZar(value) : String(value)
              }
              domain={[0, volumeDomainMax]}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 11 }}
              width={52}
            />
          )}
          <Tooltip
            content={mode === 'price' ? <CustomTooltipPrice /> : <CustomTooltipVolume />}
            cursor={{ stroke: '#E5E7EB', strokeWidth: 1 }}
          />
          {(market.market_options ?? []).map((option: MarketOption, index: number) => (
            <Line
              key={option.id}
              type="monotone"
              dataKey={option.name}
              stroke={colors[index % colors.length]}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5, strokeWidth: 0 }}
              name={option.name}
            />
          ))}
          {mode === 'price' &&
            (market.market_options ?? []).map((option: MarketOption, index: number) => {
              const y = avgBuyByOptionPercent?.[option.id];
              if (y === undefined || !Number.isFinite(y) || y <= 0) return null;
              return (
                <ReferenceLine
                  key={`avg-buy-${option.id}`}
                  y={y}
                  stroke={colors[index % colors.length]}
                  strokeDasharray="4 4"
                  strokeOpacity={0.8}
                  ifOverflow="extendDomain"
                >
                  <Label
                    value="Avg Buy"
                    position="insideBottomRight"
                    fill="#6B7280"
                    fontSize={11}
                  />
                </ReferenceLine>
              );
            })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
